import { Component, OnInit } from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';
import {HttpErrorResponse} from '@angular/common/http';
import {ResponseModel} from '../../../../DataModels/response-model';
import {Observable} from '../../../../../../node_modules/rxjs';
import {ItemTypeModel} from '../../../../DataModels/item-type-model';
import {Title} from '@angular/platform-browser';

@Component({
  selector: 'app-item-type-form',
  templateUrl: './item-type-form.component.html',
  styleUrls: ['./item-type-form.component.scss'],
  standalone: false
})
export class ItemTypeFormComponent implements OnInit {

  itemTypeID;
  itemType: ItemTypeModel;
  itemTypeForm: UntypedFormGroup;
  itemType$: Observable<ItemTypeModel[]>;
  nonParentedTypeList: ItemTypeModel[];
  submitted = false;
  disableDelete = false;
  isSaveNew = false;
  originalSnapshot: any = {};

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title
  ) {
    this.createForm();
    this.itemTypeID = this.route.snapshot.params.id;
  }

  get f() {
    return this.itemTypeForm.controls;
  }

  createForm() {
    this.itemTypeForm = this.formBuilder.group({
      id: new UntypedFormControl(''),
      name: new UntypedFormControl(''),
      parent_id: new UntypedFormControl(''),
      active: new UntypedFormControl(true),
      allow_edit: new UntypedFormControl(true),
    });
  }

  ngOnInit() {
    this.titleService.setTitle($localize`:Item Type @@itemType:Item Type`);
    this.LoadNonParentedTypes();
    this.table.getApiListData('item_type', '?pageNumber=0&pageSize=999999').subscribe((res: any) => {
      this.itemType$ = res.Data;
    });
    if (this.itemTypeID) {
      this.LoadData();
      this.buttonConfig.reset = false;
    } else {
      Promise.resolve().then(() => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
    }
  }

  private LoadData() {
    this.table.getData('item_type', this.itemTypeID).subscribe(
      (result: any) => {
        // Handle both wrapped { Data: T } and raw T responses
        const data = (result && result.Data !== undefined) ? result.Data : result;
        this.itemType = data as ItemTypeModel;
        if (!this.itemType.allow_edit) {
          this.disableDelete = true;
        }
        // Patch the form as soon as data is available
        this.itemTypeForm.patchValue(this.itemType);
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      },
      (error: HttpErrorResponse) => {
        console.log(error);
      }
    );
  }

  private LoadNonParentedTypes() {
    this.table.getApiListData('item_type', '?pageNumber=0&pageSize=999999').subscribe(
      (result: ResponseModel) => {
        const tmpArray: ItemTypeModel[] = result.Data;
        this.nonParentedTypeList = tmpArray.filter(x => x.parent_id === '');
      },
      (error: HttpErrorResponse) => {
        console.log(error);
      },
      () => {
      }
    );
  }

  saveClose() {
    if (this.onSave(true)) {
    }
  }

  saveNew() {
    // Clear only after async save succeeds
    this.isSaveNew = true;
    this.onSave(false);
  }

  resetForm() {
    this.resetToDefaults();
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  onClose() {
    this.router.navigate(['admin/item-type-list']).then();
  }

  private resetToDefaults(): void {
    // Reset form to known defaults and clear component state for a fresh entry
    this.itemTypeForm.reset({
      id: null,
      name: '',
      parent_id: '',
      active: true,
      allow_edit: true,
    });
    this.itemType = {id: null, name: '', parent_id: '', active: true, allow_edit: true} as ItemTypeModel;
    this.itemTypeID = null;
    this.submitted = false;
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete item type? @@areYouSureYouWantToDeleteItemType:Are you sure you want to delete item type?`,
      this.itemType.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('item_type', this.itemTypeID).subscribe(
          (result) => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          (error: HttpErrorResponse) => {
            // this.notification.error($localize`:Error @@error:Error`);
            console.log(error);
          },
          () => {
            this.onClose();
          }
        );
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
      }
    );
  }

  onSave(saveClose: boolean) {
    this.submitted = true;
    if (this.itemTypeForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors! @@formErrors:Form Errors!`);
      this.submitted = false;
      return false;
    }

    this.itemType = Object.assign({}, this.itemTypeForm.value);

    if (this.itemTypeID) {
      if (this.itemTypeForm.invalid === false) {
        this.table.patch('item_type', this.itemTypeID, this.itemType).then(
          () => {
            try {
              this.originalSnapshot = this.buildSnapshot();
            } catch {
            }
            if (saveClose) {
              this.onClose();
            } else if (this.isSaveNew) {
              this.isSaveNew = false;
              this.resetToDefaults();
            }
          },
          (error: HttpErrorResponse) => {
            console.log(error);
          }
        ).finally(() => {
          this.submitted = false;
        });
      }
    } else {
      this.table.postWithPromise('item_type', this.itemType).then((res) => {
        // stops double posting when clicking Save
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.itemTypeID = apiReturn?.id;
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        if (saveClose) {
          this.onClose();
        } else if (this.isSaveNew) {
          this.isSaveNew = false;
          this.resetToDefaults();
        }
      }).catch((error: HttpErrorResponse) => {
        console.log(error);
      }).finally(() => {
        this.submitted = false;
      });
    }

    return true;
  }

  buildSnapshot(): any {
    try {
      return this.itemTypeForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }
}
